import { useState } from "react";
import { http, getErr } from "../../services/apiClient";
import { fmtRel } from "../../utils/dates";
import { C } from "../../utils/statusMappings";
import { Badge, Btn, Card, Input, Modal, Row, SH, StatusBadge, Toast, useToast } from "../../components/legacy-primitives";

export const LOST_REASONS = ["Price too high", "Date unavailable", "Design not possible", "Customer not responding", "Chose competitor", "Budget mismatch", "Just browsing", "Other"];

export const NewInquiryForm = ({ onClose, onSuccess }: any) => {
  const { show, t } = useToast();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<any>({ customerName: "", phone: "", source: "WhatsApp", occasion: "", expectedValue: "", notes: "", conversationSummary: "", nextAction: "", nextFollowUpAt: "" });
  const set = (k: string) => (v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  return (
    <Modal title="New Customer Inquiry" subtitle="Capture customer info and first conversation before it gets lost in WhatsApp." onClose={onClose} width={680}>
      {t && <Toast msg={t.msg} type={t.type} onClose={() => {}} />}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <Input label="Customer Name" value={form.customerName} onChange={set("customerName")} required />
        <Input label="WhatsApp / Phone" value={form.phone} onChange={(v: string) => set("phone")(v.replace(/\D/g, "").slice(0, 10))} required placeholder="10 digit number" />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <Input label="Source" value={form.source} onChange={set("source")} options={["WhatsApp", "Instagram", "Facebook", "Google", "Walk-in", "Referral", "Other"]} />
        <Input label="Occasion" value={form.occasion} onChange={set("occasion")} options={["Birthday", "Anniversary", "Wedding", "Corporate", "Baby Shower", "Other"]} />
      </div>
      <Input label="Expected Sale Value" value={form.expectedValue} onChange={set("expectedValue")} type="number" suffix="₹" />
      <Input label="Customer Requirement" value={form.notes} onChange={set("notes")} rows={3} placeholder="Cake type, size, delivery date, design, budget, etc." />
      <Input label="Conversation Update" value={form.conversationSummary} onChange={set("conversationSummary")} rows={3} placeholder="What did the customer ask? What did Sales reply? Current decision state." />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <Input label="Next Action" value={form.nextAction} onChange={set("nextAction")} placeholder="Send quote, call back, wait for design..." />
        <Input label="Next Follow Up" value={form.nextFollowUpAt} onChange={set("nextFollowUpAt")} type="datetime-local" />
      </div>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", borderTop: `1px solid ${C.border}`, paddingTop: 16 }}>
        <Btn v="ghost" onClick={onClose}>Cancel</Btn>
        <Btn loading={loading} onClick={async () => {
          if (
            !form.customerName ||
            !/^[6-9]\d{9}$/.test(form.phone.replace(/\D/g, ""))
          ) {
            show(
              "Customer name and a valid 10-digit Indian mobile number are required",
              "error",
            );
            return;
          }
          setLoading(true);
          try {
            await http.post("/inquiries", {
              customerName: form.customerName,
              phone: form.phone,
              source: form.source,
              occasion: form.occasion,
              notes: form.notes,
              expectedValuePaise: form.expectedValue ? Math.round(Number(form.expectedValue) * 100) : undefined,
              conversationSummary: form.conversationSummary,
              nextAction: form.nextAction,
              nextFollowUpAt: form.nextFollowUpAt || undefined,
            });
            show("Inquiry logged");
            onSuccess?.();
            onClose();
          } catch (e: any) { show(getErr(e), "error"); } finally { setLoading(false); }
        }}>Save Inquiry</Btn>
      </div>
    </Modal>
  );
};

export const InquiryConversationModal = ({ inquiry, onClose, onSuccess }: any) => {
  const { show, t } = useToast();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<any>({ summary: "", channel: "WHATSAPP", customerSentiment: "Interested", nextAction: "", nextFollowUpAt: "", status: "", saleHappened: "", lostReason: "", lostReasonNotes: "" });
  const set = (k: string) => (v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  const needsReason = form.status === "LOST" || form.saleHappened === "NO";

  const statusTransitions: Record<string, string[]> = {
    NEW: ["CONTACTED", "REQUIREMENT_TAKEN", "LOST", "DORMANT"],
    CONTACTED: ["REQUIREMENT_TAKEN", "QUOTED", "FOLLOWUP_DUE", "NEGOTIATION", "LOST", "DORMANT"],
    REQUIREMENT_TAKEN: ["QUOTED", "FOLLOWUP_DUE", "NEGOTIATION", "LOST", "DORMANT"],
    QUOTED: ["NEGOTIATION", "ADVANCE_PENDING", "CONVERTED", "FOLLOWUP_DUE", "LOST", "DORMANT"],
    FOLLOWUP_DUE: ["CONTACTED", "NEGOTIATION", "ADVANCE_PENDING", "CONVERTED", "LOST", "DORMANT"],
    NEGOTIATION: ["QUOTED", "ADVANCE_PENDING", "CONVERTED", "FOLLOWUP_DUE", "LOST"],
    ADVANCE_PENDING: ["CONVERTED", "FOLLOWUP_DUE", "NEGOTIATION", "LOST"],
    CONVERTED: ["REOPENED"],
    LOST: ["REOPENED", "REACTIVATION_ALLOWED"],
    DORMANT: ["REACTIVATION_ALLOWED", "REOPENED", "LOST"],
    REACTIVATION_ALLOWED: ["REOPENED", "CONTACTED", "LOST"],
    REOPENED: ["CONTACTED", "REQUIREMENT_TAKEN", "QUOTED", "FOLLOWUP_DUE", "LOST"],
  };

  const allowedStatuses = statusTransitions[inquiry.status] || [];

  return (
    <Modal title={`Update ${inquiry.inquiryNumber}`} subtitle={`${inquiry.customerName} · +91 ${inquiry.phone}`} onClose={onClose} width={720}>
      {t && <Toast msg={t.msg} type={t.type} onClose={() => {}} />}
      <Card style={{ marginBottom: 16, background: C.bg }}>
        <Row label="Current Status" value={<StatusBadge status={inquiry.status} />} />
        <Row label="Requirement" value={inquiry.notes || "No requirement captured"} />
        <Row label="Last Update" value={inquiry.lastConversationAt ? fmtRel(inquiry.lastConversationAt) : "No update yet"} />
      </Card>
      <Input label="Conversation Update" value={form.summary} onChange={set("summary")} rows={4} required placeholder="Write what happened in today's customer conversation." />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <Input label="Channel" value={form.channel} onChange={set("channel")} options={["WHATSAPP", "CALL", "INSTAGRAM", "STORE", "EMAIL"]} />
        <Input label="Customer Sentiment" value={form.customerSentiment} onChange={set("customerSentiment")} options={["Interested", "Price Sensitive", "Needs Design", "Urgent", "Cold", "Not Responding"]} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <Input label="Update Status" value={form.status} onChange={set("status")} options={allowedStatuses} />
        <Input label="Did Sale Happen?" value={form.saleHappened} onChange={set("saleHappened")} options={["YES", "NO", "PENDING"]} />
      </div>
      {needsReason && (
        <div style={{ background: "#FFF4F2", border: `1px solid ${C.danger}44`, borderRadius: 16, padding: 14, marginBottom: 14 }}>
          <Input label="Reason Sale Did Not Happen" value={form.lostReason} onChange={set("lostReason")} options={LOST_REASONS} required />
          <Input label="Reason Notes" value={form.lostReasonNotes} onChange={set("lostReasonNotes")} rows={2} placeholder="Add price quoted, competitor, date issue, customer objection, etc." />
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <Input label="Next Action" value={form.nextAction} onChange={set("nextAction")} placeholder="Call tomorrow, send design, collect advance..." />
        <Input label="Next Follow Up" value={form.nextFollowUpAt} onChange={set("nextFollowUpAt")} type="datetime-local" />
      </div>
      {!!inquiry.conversationUpdates?.length && (
        <Card style={{ marginBottom: 16 }}>
          <SH title="Recent Conversation Updates" />
          {inquiry.conversationUpdates.map((u: any) => (
            <div key={u.id} style={{ borderBottom: `1px solid ${C.border}`, padding: "8px 0" }}>
              <div style={{ fontSize: 12, color: C.mid }}>{fmtRel(u.conversationAt)} · {u.channel}</div>
              <div style={{ fontSize: 13, color: C.text, marginTop: 3 }}>{u.summary}</div>
              {u.lostReason && <Badge color={C.danger}>{u.lostReason}</Badge>}
            </div>
          ))}
        </Card>
      )}
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", borderTop: `1px solid ${C.border}`, paddingTop: 16 }}>
        <Btn v="ghost" onClick={onClose}>Cancel</Btn>
        <Btn loading={loading} onClick={async () => {
          if (!form.summary.trim()) { show("Conversation update is required", "error"); return; }
          if (needsReason && !form.lostReason) { show("Lost reason is required when sale did not happen", "error"); return; }
          setLoading(true);
          try {
            const response: any = await http.post(`/inquiries/${inquiry.id}/conversation-updates`, {
              ...form,
              status: form.status || undefined,
              saleHappened: form.saleHappened === "YES" ? true : form.saleHappened === "NO" ? false : undefined,
              nextFollowUpAt: form.nextFollowUpAt ? new Date(form.nextFollowUpAt).toISOString() : undefined,
            });

            if (response?.data?.success === false) {
              throw new Error(response?.data?.error?.message || "Conversation update was not saved");
            }

            await onSuccess?.();
            show("Conversation update saved");
            onClose();
          } catch (e: any) { show(getErr(e), "error"); } finally { setLoading(false); }
        }}>Save Update</Btn>
      </div>
    </Modal>
  );
};
